import { useState, type FormEvent } from 'react'
import { useCreateActivity, useUpdateActivity } from '../hooks/useActivities'
import {
  ModalShell, Field, inputClass, ServerError,
  CancelButton, PrimaryButton, DangerButton
} from './modal-form'
import type { Activity, ActivityCreate, Attachment } from '../types'

// ActivityEditor - one modal for both create and edit. `activity` null = new;
// otherwise pre-fills from the existing record. Same visual pattern as
// EditEventModal (ModalShell + Field + inputClass).
//
// Attachments v1: URL-only ({label, url}). Add/remove rows inline; client-side
// validates the URL scheme (http/https/mailto) so the server gets clean data.
// Future: Blob-backed file uploads via SharePoint or Azure Blob — when that
// arrives, Attachment grows a kind/sizeBytes/mimeType and the row UI grows a
// tabbed picker (Link / File / Image), but the wire shape stays compatible.
//
// Delete is exposed as an optional onDelete prop. The parent (ActivitiesTab)
// decides whether to show it (only when editing, never on create) and handles
// the confirm + mutation. Keeps this component free of the delete hook for the
// create path that doesn't need it.

interface Props {
  eventId: number
  activity: Activity | null   // null = creating new
  onClose: () => void
  onDelete?: () => void       // omit to hide the Delete button (e.g. on create)
  deleting?: boolean          // show pending state on the Delete button
}

const ATTACHMENT_URL_RE = /^(https?:\/\/|mailto:)/i

export function ActivityEditor({ eventId, activity, onClose, onDelete, deleting }: Props) {
  const isEditing = activity !== null
  const createMut = useCreateActivity(eventId)
  const updateMut = useUpdateActivity(eventId)
  const mut = isEditing ? updateMut : createMut

  const [name, setName] = useState(activity?.name ?? '')
  const [description, setDescription] = useState(activity?.description ?? '')
  const [location, setLocation] = useState(activity?.location ?? '')
  const [timeText, setTimeText] = useState(activity?.timeText ?? '')
  const [staffCount, setStaffCount] = useState(
    activity?.staffCount != null ? String(activity.staffCount) : ''
  )
  const [supplier, setSupplier] = useState(activity?.supplier ?? '')
  const [attachments, setAttachments] = useState<Attachment[]>(
    activity?.attachments ?? []
  )

  const trimmedName = name.trim()
  const staffCountNum = staffCount === '' ? null : Number(staffCount)

  const fieldErrors: { name?: string; staffCount?: string } = {}
  if (trimmedName.length === 0) fieldErrors.name = 'Name is required'
  else if (trimmedName.length > 255) fieldErrors.name = 'Name must be 255 characters or fewer'
  if (
    staffCount !== '' &&
    (staffCountNum === null || !Number.isInteger(staffCountNum) || staffCountNum < 0)
  ) {
    fieldErrors.staffCount = 'Staff required must be a whole number (or leave blank)'
  }

  // Per-row attachment validation. We render the result inline next to each
  // row so the user knows which one is broken. The save button is disabled
  // until everything is clean.
  const attachmentErrors = attachments.map((a) => {
    if (a.label.trim().length === 0) return 'Label required'
    if (a.url.trim().length === 0) return 'URL required'
    if (!ATTACHMENT_URL_RE.test(a.url.trim())) return 'URL must start with http://, https://, or mailto:'
    if (a.label.length > 255) return 'Label too long'
    if (a.url.length > 2000) return 'URL too long'
    return null
  })
  const hasAttachmentErrors = attachmentErrors.some((e) => e !== null)

  const isValid = Object.keys(fieldErrors).length === 0 && !hasAttachmentErrors
  const saving = mut.isPending

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!isValid || saving) return
    const body: ActivityCreate = {
      name: trimmedName,
      description: description.trim() === '' ? null : description,
      location: location.trim() === '' ? null : location.trim(),
      timeText: timeText.trim() === '' ? null : timeText.trim(),
      staffCount: staffCountNum,
      supplier: supplier.trim() === '' ? null : supplier.trim(),
      attachments: attachments.map((a) => ({
        label: a.label.trim(),
        url: a.url.trim()
      })),
      sortOrder: activity?.sortOrder ?? 0,
      // mig 035 — preserve library-link / display fields across edit. The
      // editor doesn't expose these directly (yet); passing through keeps
      // PUT from wiping a library-pulled row's category / icon / enabled
      // state on save.
      icon: activity?.icon ?? null,
      isEnabled: activity?.isEnabled ?? true,
      category: activity?.category ?? null
    }
    if (isEditing && activity) {
      updateMut.mutate({ id: activity.id, body }, { onSuccess: () => onClose() })
    } else {
      createMut.mutate(body, { onSuccess: () => onClose() })
    }
  }

  function addAttachment() {
    setAttachments((cur) => [...cur, { label: '', url: '' }])
  }
  function removeAttachment(idx: number) {
    setAttachments((cur) => cur.filter((_, i) => i !== idx))
  }
  function updateAttachment(idx: number, field: 'label' | 'url', value: string) {
    setAttachments((cur) => cur.map((a, i) => (i === idx ? { ...a, [field]: value } : a)))
  }

  return (
    <ModalShell
      title={isEditing ? 'Edit activity' : 'Add activity'}
      onClose={onClose}
      onSubmit={handleSubmit}
      footer={
        <>
          {onDelete && (
            <DangerButton onClick={onDelete} disabled={saving || deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </DangerButton>
          )}
          <CancelButton onClick={onClose} disabled={saving || deleting} />
          <PrimaryButton disabled={!isValid || saving || deleting}>
            {saving ? 'Saving…' : isEditing ? 'Save changes' : 'Add activity'}
          </PrimaryButton>
        </>
      }
      body={
        <div className="space-y-4">
          <Field label="Activity name" error={fieldErrors.name}>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={255}
              autoFocus
              placeholder="e.g. Storytelling in the Reading Room"
              className={inputClass(!!fieldErrors.name)}
            />
          </Field>

          <Field label="Description" hint="What happens? Who's it for?">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className={`${inputClass(false)} resize-y font-body`}
              placeholder="Optional - any detail volunteers should know to talk about this with visitors."
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Location">
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                maxLength={255}
                placeholder="e.g. Reading Room, Mulberries Lawn"
                className={inputClass(false)}
              />
            </Field>
            <Field label="Times" hint="Free text">
              <input
                type="text"
                value={timeText}
                onChange={(e) => setTimeText(e.target.value)}
                maxLength={255}
                placeholder="e.g. 11:00 / 13:00 / 15:00"
                className={inputClass(false)}
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Staff required" error={fieldErrors.staffCount}>
              <input
                type="number"
                min="0"
                step="1"
                value={staffCount}
                onChange={(e) => setStaffCount(e.target.value)}
                placeholder="—"
                className={inputClass(!!fieldErrors.staffCount)}
              />
            </Field>
            <Field label="Supplier / In-house">
              <input
                type="text"
                value={supplier}
                onChange={(e) => setSupplier(e.target.value)}
                maxLength={255}
                placeholder="e.g. Wise Owl Theatre Co."
                className={inputClass(false)}
              />
            </Field>
          </div>

          {/* Attachments section - URL-only for v1 */}
          <div>
            <div className="flex items-baseline justify-between mb-1.5">
              <span className="text-xs uppercase tracking-wide font-semibold text-ink-soft">
                Attachments
              </span>
              <button
                type="button"
                onClick={addAttachment}
                className="text-xs text-green font-semibold hover:underline"
              >
                + Add link
              </button>
            </div>
            {attachments.length === 0 ? (
              <div className="text-xs italic text-ink-soft bg-parchment border border-line rounded px-3 py-3">
                No attachments. Add a link to a Drive doc, supplier brief, or anything else.
              </div>
            ) : (
              <div className="space-y-2">
                {attachments.map((a, idx) => {
                  const rowError = attachmentErrors[idx]
                  return (
                    <div key={idx}>
                      <div className="flex gap-2 items-start">
                        <input
                          type="text"
                          value={a.label}
                          onChange={(e) => updateAttachment(idx, 'label', e.target.value)}
                          placeholder="Label"
                          maxLength={255}
                          className={`${inputClass(rowError !== null)} flex-1`}
                        />
                        <input
                          type="url"
                          value={a.url}
                          onChange={(e) => updateAttachment(idx, 'url', e.target.value)}
                          placeholder="https://…"
                          maxLength={2000}
                          className={`${inputClass(rowError !== null)} flex-[2]`}
                        />
                        <button
                          type="button"
                          onClick={() => removeAttachment(idx)}
                          className="text-ink-soft hover:text-oxblood text-xl leading-none w-9 h-9 flex items-center justify-center rounded hover:bg-parchment flex-shrink-0"
                          aria-label="Remove attachment"
                        >
                          ×
                        </button>
                      </div>
                      {rowError && (
                        <div className="text-xs text-oxblood mt-1 ml-1">{rowError}</div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {mut.isError && (
            <ServerError
              title={`Could not ${isEditing ? 'save' : 'add'} activity`}
              error={mut.error as Error}
            />
          )}
        </div>
      }
    />
  )
}
